import React, { useState } from 'react';
import { DaySequence } from '@core/generated/gql-types';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import DaySequenceDrawer from '@/components/daySequence/DaySequenceDrawer';
import { useDaySequenceUtils } from '@/components/daySequence/useDaySequenceUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarClock } from '@fortawesome/pro-regular-svg-icons';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';

interface TimesheetDisputesTableProps {
  disputedTimesheets: DaySequence[];
}

const TimesheetDisputesTable = ({
  disputedTimesheets,
}: TimesheetDisputesTableProps) => {
  const [selectedDaySequenceId, setSelectedDaySequenceId] = useState<
    string | null
  >(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { getDaySequenceById } = useDaySequenceStore();
  const selectedDaySequence = selectedDaySequenceId
    ? getDaySequenceById(selectedDaySequenceId)
    : null;

  const {
    getStatusBadgeClass,
    getStatusLabel,
    formatDate,
    getWorkerName,
    getClientName,
  } = useDaySequenceUtils();
  const { getTerm } = useTemplateStore();

  const handleRowClick = (daySequence: DaySequence) => {
    setSelectedDaySequenceId(daySequence.id);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedDaySequenceId(null);
    setIsEditMode(false);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  return (
    <div className="flex h-[500px] flex-col rounded-xl bg-white p-4 shadow">
      <div className="text-md mb-2 h-6 font-semibold text-blue-900">
        Timesheet Disputes{' '}
        <span className="text-sm">({disputedTimesheets.length})</span>
      </div>
      {disputedTimesheets.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="mb-6 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faCalendarClock}
              className="text-gray-400"
              style={{ fontSize: '3rem' }}
            />
          </div>
          <p className="mb-2 text-lg font-semibold text-gray-900">
            No timesheet disputes
          </p>
          <p className="max-w-sm text-center text-sm text-gray-500">
            When {getTerm(TemplateTerm.WORKER, true)} dispute their timesheets,
            they will appear here for review and resolution.
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-t border-gray-200 text-xs text-gray-500">
                <th className="py-2 text-left font-medium">
                  {getTerm(TemplateTerm.WORKER, false)}
                </th>
                <th className="py-2 text-left font-medium">
                  {getTerm(TemplateTerm.CLIENT, false)}
                </th>
                <th className="py-2 text-left font-medium">Date</th>
                <th className="py-2 text-center font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {disputedTimesheets.map((daySequence) => {
                const workerName = getWorkerName(daySequence);
                const clientName = getClientName(daySequence);
                const formattedDate = formatDate(daySequence.startDate);
                const initials = workerName
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2);

                return (
                  <tr
                    key={daySequence.id}
                    onClick={() => handleRowClick(daySequence)}
                    className="cursor-pointer transition-colors hover:bg-gray-50"
                  >
                    <td className="flex items-center gap-2 py-2">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-gray-300 text-xs font-semibold text-gray-600">
                        {initials}
                      </span>
                      <span
                        className="max-w-[120px] truncate text-xs font-semibold"
                        title={workerName}
                      >
                        {workerName.length > 15
                          ? `${workerName.slice(0, 15)}...`
                          : workerName}
                      </span>
                    </td>
                    <td
                      className="max-w-[150px] truncate py-2 text-xs font-semibold"
                      title={clientName}
                    >
                      {clientName}
                    </td>
                    <td className="py-2 text-xs">{formattedDate}</td>
                    <td className="py-2 text-center">
                      <span
                        className={`rounded-full px-4 py-1 text-xs font-semibold ${getStatusBadgeClass(daySequence.status)}`}
                      >
                        {getStatusLabel(daySequence.status)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DaySequenceDrawer
        daySequence={selectedDaySequence ?? null}
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        isEditMode={isEditMode}
        onEdit={handleEdit}
      />
    </div>
  );
};

export default TimesheetDisputesTable;
